import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import {
  ShieldCheck,
  FolderOpen,
  KeyRound,
} from "lucide-react";
import type { Artist } from "../data";
import { ContentPanel, MetricCard } from "./shared";

interface Props {
  selectedArtist: Artist;
}

export default function Access({ selectedArtist }: Props) {
  const setTemporaryPassword = useMutation(api.auth.setTemporaryPassword);
  const [tempPassword, setTempPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState("");
  const [generated, setGenerated] = useState("");

  useEffect(() => {
    setTempPassword("");
    setStatus("idle");
    setError("");
    setGenerated("");
  }, [selectedArtist.id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedArtist.email) return;
    setStatus("saving");
    setError("");
    setGenerated("");
    try {
      const result = await setTemporaryPassword({
        email: selectedArtist.email,
        temporaryPassword: tempPassword.trim() || undefined,
      });
      setGenerated((result as any).temporaryPassword);
      setTempPassword("");
      setStatus("saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not set temporary password");
      setStatus("error");
    }
  }

  return (
    <div className="space-stack">
      <ContentPanel title="Access" eyebrow="Admin setup">
        <div className="access-grid">
          <MetricCard icon={<ShieldCheck className="h-5 w-5" />} label="Admin role" value="BJ" />
          <MetricCard icon={<FolderOpen className="h-5 w-5" />} label="Artist role" value={selectedArtist.name} />
          <MetricCard icon={<FolderOpen className="h-5 w-5" />} label="Storage" value="Drive" />
        </div>
        <p className="muted-copy">
          Artist access now uses an admin-issued temporary password. The artist is forced to create their own password after signing in.
        </p>
      </ContentPanel>

      <ContentPanel title="Temporary password" eyebrow={selectedArtist.name}>
        <form className="password-form" onSubmit={handleSubmit}>
          <label>
            <span>Artist email</span>
            <input disabled value={selectedArtist.email ?? "Add an artist email first"} />
          </label>
          <label>
            <span>Temporary password</span>
            <input
              autoComplete="new-password"
              onChange={(e) => setTempPassword(e.target.value)}
              placeholder="Leave blank to generate"
              type="text"
              value={tempPassword}
            />
          </label>
          <div className="stem-form-actions">
            {status === "saved" ? <span>Temporary password ready</span> : null}
            {status === "error" ? <span>{error}</span> : null}
            <button disabled={!selectedArtist.email || status === "saving"} type="submit">
              <KeyRound className="h-4 w-4" />
              {status === "saving" ? "Setting password" : "Set temporary password"}
            </button>
          </div>
        </form>
        {generated ? (
          <div className="temporary-password-result">
            <span>Temporary password</span>
            <strong>{generated}</strong>
            <button
              onClick={() => navigator.clipboard?.writeText(generated)}
              type="button"
            >
              Copy
            </button>
          </div>
        ) : null}
      </ContentPanel>
    </div>
  );
}